import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { AccountRegistryService, PosAccountOption } from '../../services/account-registry.service';
import { FormsModule } from '@angular/forms';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { Menu, MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Customer, CustomerLoyaltyData } from '../../modals/product.modals';
import { User } from '../../modals/user.model';
import { AuthService } from '../../services/auth.service';
import {
  CashDenominationService,
  PunchStatus,
} from '../../services/cash-denomination.service';
import {
  ClientSettingsService,
  ModePermissions,
} from '../../services/client-settings.service';
import { FormDataService } from '../../services/form-data.service';
import { IndustryService } from '../../services/industry.service';
import { KeyboardEventService } from '../../services/keyboard-event.service';
import { NetworkNotificationService } from '../../services/network-notification.service';
import {
  OfferGroupService,
  PricingGroup,
} from '../../services/offer-group.service';
import { ProductDbService } from '../../services/productdb.service';
import { CashDenominationComponent } from '../cash-denomination/cash-denomination.component';
import { CustomerFormComponent } from '../components/customer-form/customer-form.component';
import { NetworkStatusIndicatorComponent } from '../components/network-status-indicator/network-status-indicator.component';
import { ModeIndicatorComponent, OrderMode } from './header/mode-indicator/mode-indicator.component';
import { AnnouncementMarqueeComponent } from '../shared/components/announcement-marquee/announcement-marquee.component';
import { FestiveDoodleComponent } from '../shared/components/festive-doodle/festive-doodle.component';
import { FestiveFireworksComponent } from '../shared/components/festive-fireworks/festive-fireworks.component';
import { FestiveLanternComponent } from '../shared/components/festive-lantern/festive-lantern.component';
import { FestiveService } from '../../services/festive.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    CommonModule,
    HttpClientModule,
    CalendarModule,
    DialogModule,
    FormsModule,
    ModeIndicatorComponent,
    DropdownModule,
    InputSwitchModule,
    MenuModule,
    AutoCompleteModule,
    CustomerFormComponent,
    CashDenominationComponent,
    NetworkStatusIndicatorComponent,
    ToastModule,
    AnnouncementMarqueeComponent,
    FestiveDoodleComponent,
    FestiveFireworksComponent,
    FestiveLanternComponent,
  ],
  templateUrl: './layout.component.html',
  providers: [
    ClientSettingsService,
    ProductDbService,
    CashDenominationService,
    MessageService, // Used by the network toast (<p-toast key="network">)
    // IndustryService,
    NetworkNotificationService,
  ],
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('userMenu') userMenu!: Menu;
  @ViewChild('customerNameAutocomplete') customerNameAutocomplete: any;
  @ViewChild('mobileNumberAutocomplete') mobileNumberAutocomplete: any;

  header = true;
  currentDate: Date = new Date(); // Initialize with current date
  maxDate: Date = new Date(); // Prevent future dates
  businessDate: Date = new Date(); // Initialize with current date
  mobileNumber: string = '';
  customerName: string = '';
  salesRep: string = '';
  userName!: string;

  // Festival fireworks over the billing screen, once per user per day right after sign-in
  private readonly festiveService = inject(FestiveService);
  celebrationGreeting = '';
  storeCode!: string;
  empCode!: string;
  creditOutstanding: string = '0.00';
  canHighlight!: boolean;
  showFooter: boolean = true;
  randomAvatar: string = '';
  isQuotationMode: boolean = false;
  isDateEditMode: boolean = false;

  sessionWarningMessage: string = '';
  showSessionWarning: boolean = false;
  sessionWarningType: 'timeout' | 'outside' | 'error' | 'critical' = 'timeout';
  private sessionWarningTimeout: any = null;
  private jwtRefreshTimeout: any = null;
  private countdownInterval: any = null;
  private refreshTokenRetryCount: number = 0;

  // Customer payment due properties
  currentCustomer: Customer | null = null;
  isSuspenseMode: boolean = false;
  isWholesaleMode: boolean = false;
  isOfferGroupMode: boolean = false;

  // Loyalty points properties
  customerLoyaltyData: CustomerLoyaltyData | null = null;
  loyaltyPoints: number = 0;
  loyaltyAmount: number = 0;
  isLoyaltyEnabled: boolean = false;
  isExchangeOrder: boolean = false;
  isDeleteMode: boolean = false;
  showKeyboardShortcuts: boolean = false;
  isBarcodeEnabled: boolean = true; // Barcode visibility from client settings
  isMobileMandatory: boolean = false; // Mobile number mandatory from client settings
  showUserMenu: boolean = false;
  isCounterSessionEnabled: boolean = false; // Counter session monitoring from client settings
  isSwitchCompanyEnabled: boolean = false;
  isSwitchCompany: boolean = false;
  /** Switch Company: accounts signed in on this browser */
  showAccountList: boolean = false;
  posAccounts: PosAccountOption[] = [];
  private readonly accountRegistry = inject(AccountRegistryService);

  /** Cash Denomination availability (feature-flags.ts) */
  get isDenominationEnabled(): boolean {
    return this.industryService.isDenominationEnabled();
  }
  userMenuItems: any[] = [];
  weekdays: string[] = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  currentDayIndex: number = 0;
  modePermissions: ModePermissions = {
    wholesaleMode: false,
    quotationMode: false,
    suspenseMode: false,
    pricingGroupMode: false,
  };

  // Offer Group properties
  pricingGroups: PricingGroup[] = [];
  selectedOfferGroupId: number | null = null;
  private formDataSubscription!: Subscription;
  private authSubscription!: Subscription;
  selectedFooterBox: ElementRef | null = null; // To track the selected footer box

  // Customer autocomplete properties
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  filteredCustomersForMobile: Customer[] = [];

  // Customer form dialog properties
  showCustomerDialog: boolean = false;
  customerDialogMode: 'add' | 'edit' = 'add';

  showShippingDialog: boolean = false;
  isShippingDetailsEnabled: boolean = false;
  shippingAddress1: string = '';
  shippingAddress2: string = '';
  shippingCity: string = '';
  shippingState: string = '';
  shippingPincode: string = '';
  /** Pincode: digits only, exactly 6 when provided (no existence or State/City check) */
  shippingPincodeCharsBlocked = false;
  shippingPincodeTouched = false;
  shippingSaveAttempted = false;
  transportLrNo: string = '';
  transportVehicleNo: string = '';
  transporterName: string = '';
  transportDeliveryChallan: string = '';

  showCashOutDialog: boolean = false;
  punchStatus: PunchStatus | null = null;
  pendingLogoutAction: 'normal' | 'force' | null = null;
  canSkipCashOut: boolean = true;

  private smileyEmojis = [
    '😀',
    '😃',
    '😄',
    '😁',
    '😆',
    '😊',
    '😇',
    '🙂',
    '🙃',
    '😉',
    '😍',
    '🥰',
    '😘',
    '😙',
    '😋',
    '😛',
    '😝',
    '😜',
    '😎',
    '🤩',
    '🥳',
  ];

  private formDataSubject = new Subject<{
    currentDate: Date | null;
    empCode: string;
    storeCode: string;
    mobileNumber: string;
    customerName: string;
    salesRep: string;
  }>();

  private jwtHelper: JwtHelperService;
  customerSearchValue: any = '';

  constructor(
    private eventService: KeyboardEventService,
    private authService: AuthService,
    private renderer: Renderer2,
    private formDataService: FormDataService,
    private el: ElementRef,
    private router: Router,
    private clientSettingsService: ClientSettingsService,
    private offerGroupService: OfferGroupService,
    private productService: ProductDbService,
    private cashDenominationService: CashDenominationService,
    private industryService: IndustryService,
    // NetworkNotificationService is injected here to bootstrap it at the root
    // layout level so it starts listening to network state changes immediately.
    // It is providedIn: 'root' and purely observational — no side effects.
    private readonly networkNotificationService: NetworkNotificationService,
  ) {
    this.jwtHelper = new JwtHelperService();

    const token = localStorage.getItem('name');
    let userInfo = JSON.parse(token || '{}');
    this.empCode = userInfo['name'] || 'Default';
    if (userInfo['accessToken']) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(
          userInfo['accessToken'],
        );
        // FIX: JWT 'name' claim may not exist — fall back to userInfo['name'] from API response.
        // OLD: this.salesRep = decodedToken?.['name'] || 'Sales Person';
        this.salesRep =
          decodedToken?.['name'] || userInfo['name'] || 'Sales Person';
      } catch (error) {
        console.error('Error decoding JWT token:', error);
        this.empCode = userInfo['name'] || 'Default';
        this.salesRep = userInfo['name'] || 'Sales Person';
      }
    } else {
      this.empCode = userInfo['name'] || 'Default';
      this.salesRep = userInfo['name'] || 'Sales Person';
    }

    if (userInfo['warehouse']) this.storeCode = userInfo['warehouse']['name'];

    this.generateRandomAvatar();
  }

  /** Active order mode for the header chip and accent bar; same precedence as the old mode badge. */
  get currentMode(): OrderMode {
    if (this.isQuotationMode) return 'quotation';
    if (this.isWholesaleMode) return 'wholesale';
    if (this.isDeleteMode) return 'delete';
    if (this.isOfferGroupMode) return 'offer';
    return null;
  }

  generateRandomAvatar(): void {
    const randomIndex = Math.floor(Math.random() * this.smileyEmojis.length);
    this.randomAvatar = this.smileyEmojis[randomIndex];
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDownEvent(event: KeyboardEvent): void {
    // Get current user directly from authService to avoid subscription timing issues
    const currentUser = this.authService.currentUserValue;
    let currentUserName =
      currentUser && currentUser.name ? currentUser.name : '';

    // Fallback: If authService doesn't have the user, get it from localStorage directly
    if (!currentUserName) {
      const localStorageUser = localStorage.getItem('name');
      if (localStorageUser) {
        try {
          const parsed = JSON.parse(localStorageUser);
          currentUserName = parsed?.name || '';
        } catch (e) {
          // Ignore parse errors
        }
      }
    }

    // Restrict all keyboard shortcuts when user is not logged in
    // Use currentUserName (from authService or localStorage) instead of this.userName to avoid subscription timing issues
    if (!currentUserName) {
      return;
    }

    // Block all F-keys when Cash Out dialog is open
    if (
      this.showCashOutDialog &&
      [
        'F1',
        'F2',
        'F3',
        'F4',
        'F5',
        'F6',
        'F7',
        'F8',
        'F9',
        'F10',
        'F11',
        'F12',
      ].includes(event.key)
    ) {
      event.preventDefault();
      return;
    }

    // Handle Ctrl+H for keyboard shortcuts help
    if (event.ctrlKey && (event.key === 'h' || event.key === 'H')) {
      event.preventDefault();
      this.loadClientSettings();
      this.showKeyboardShortcuts = true;
      return;
    }

    if (!/^F([1-9]|1[0-2])$/.test(event.key)) {
      return;
    }
    event.preventDefault();
    if (this.isQuotationMode) {
      return;
    }
    // F1-F8 are dispatched by the dashboard (KeyboardNavigationService), so here they only
    // flash their footer box; emitting them too ran every action twice. F9-F12 work from
    // any route and are dispatched here, but never from behind an open dialog.
    if (['F9', 'F10', 'F11', 'F12'].includes(event.key)) {
      if (this.isDialogOpen()) {
        return;
      }
      this.emitEvent(event.key);
    } else {
      this.highlightFooterBox(event.key);
    }
  }

  /** True while any PrimeNG dialog (including confirm dialogs) is on screen. */
  private isDialogOpen(): boolean {
    return !!document.querySelector('.p-dialog-mask .p-dialog');
  }

  ngOnInit(): void {
    const festival = this.festiveService.festival;
    if (festival && this.festiveService.takeCelebration(this.empCode)) {
      this.celebrationGreeting = `${festival.greeting}, ${this.storeCode || this.empCode}!`;
    }

    // Initialize userName synchronously from authService to avoid timing issues
    const currentUser = this.authService.currentUserValue;
    this.userName = currentUser && currentUser.name ? currentUser.name : '';

    if (!this.userName) {
      const token = localStorage.getItem('name');
      if (token) {
        try {
          const userInfo = JSON.parse(token);
          this.userName = userInfo?.name || '';
        } catch (error) {
          console.error('[LAYOUT ngOnInit] Error parsing localStorage:', error);
        }
      }
    }

    this.loadClientSettings();

    this.loadPricingGroups();

    this.loadCustomers();

    const today = new Date().getDay();
    this.currentDayIndex = today === 0 ? 6 : today - 1; // Convert Sunday (0) to 6, and shift others

    this.userMenuItems = [
      {
        separator: true,
        template: 'weekdays',
      },
      {
        separator: true,
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => {
          this.handleLogout();
        },
      },
    ];

    this.authSubscription = this.authService.currentUser.subscribe(
      (user: User) => {
        this.userName = user && user.name ? user.name : '';
        this.empCode = user && user.name ? user.name : 'Default';

        if (user && user.accessToken) {
          try {
            const decodedToken = this.jwtHelper.decodeToken(user.accessToken);
            // FIX: JWT 'name' claim may not exist — fall back to user.name from API response.
            // OLD: this.salesRep = decodedToken?.['name'] || 'Sales Person';
            this.salesRep =
              decodedToken?.['name'] || user?.name || 'Sales Person';
          } catch (error) {
            // OLD: this.salesRep = 'Sales Person';
            this.salesRep = user?.name || 'Sales Person';
          }
        } else {
          // OLD: this.salesRep = 'Sales Person';
          this.salesRep = user?.name || 'Sales Person';
        }
      },
    );

    this.offerGroupService.selectedGroup$.subscribe((groupId) => {
      this.selectedOfferGroupId = groupId;
      // console.log('Layout: selectedGroup$ updated:', groupId);
    });

    this.formDataSubscription = this.formDataService.formData$.subscribe(
      (formData) => {
        this.mobileNumber = formData.mobileNumber || '';
        this.customerName = formData.customerName || '';
        // FIX: Only overwrite salesRep from formData when it is non-empty.
        // Previously, an empty formData.salesRep would wipe the JWT-decoded name.
        if (formData.salesRep) this.salesRep = formData.salesRep;
        // OLD: this.salesRep = formData.salesRep || '';

        if (formData.currentDate !== undefined) {
          this.currentDate = formData.currentDate || new Date();
        }

        if (!formData.mobileNumber || formData.mobileNumber.trim() === '') {
          this.creditOutstanding = '0.00';
          this.currentCustomer = null;
          this.resetLoyaltyData();
        }

        // FIX: PrimeNG p-autoComplete caches its display value internally and
        // does NOT update the visible input when [(ngModel)] changes programmatically.
        // After payment clears customer info, we must explicitly flush the native
        // input element so the header shows blank instead of the previous name/number.
        // ONLY clear when BOTH customerName and mobileNumber are empty.
        const isNameEmpty =
          !formData.customerName || formData.customerName.trim() === '';
        const isMobileEmpty =
          !formData.mobileNumber || formData.mobileNumber.trim() === '';
        if (isNameEmpty && isMobileEmpty) {
          this.customerSearchValue = '';
          this.clearAutoCompleteInput(this.customerNameAutocomplete);
          this.clearAutoCompleteInput(this.mobileNumberAutocomplete);
        }

        if (formData.isQuotationMode !== undefined) {
          const wasQuotationMode = this.isQuotationMode;
          this.isQuotationMode = formData.isQuotationMode;

          // Reset date to current when entering quotation mode
          if (!wasQuotationMode && this.isQuotationMode) {
            this.currentDate = new Date();
            this.maxDate = new Date();
          }
        }
        if (formData.isSuspenseMode !== undefined) {
          this.isSuspenseMode = formData.isSuspenseMode;
        }
        if (formData.isWholesaleMode !== undefined) {
          this.isWholesaleMode = formData.isWholesaleMode;
        }
        if (formData.isOfferGroupMode !== undefined) {
          this.isOfferGroupMode = formData.isOfferGroupMode;
        }
        if (formData.isExchangeOrder !== undefined) {
          this.isExchangeOrder = formData.isExchangeOrder;
        }
        if (formData.isDeleteMode !== undefined) {
          this.isDeleteMode = formData.isDeleteMode;
        }
        // isDateEditMode now comes from client settings, not formData
      },
    );

    // Subscribe to shipping details changes to sync form fields
    this.formDataService.shippingDetails$.subscribe((shippingDetails) => {
      // Parse shipping address (semicolon-separated: address1;address2;city;state;pincode)
      const addressParts = (shippingDetails.shippingaddress || '').split(';');
      this.shippingAddress1 = addressParts[0] || '';
      this.shippingAddress2 = addressParts[1] || '';
      this.shippingCity = addressParts[2] || '';
      this.shippingState = addressParts[3] || '';
      this.shippingPincode = addressParts[4] || '';
      this.transportLrNo = shippingDetails.lrno || '';
      this.transportVehicleNo = shippingDetails.vehicleno || '';
      this.transporterName = shippingDetails.transportername || '';
      this.transportDeliveryChallan = shippingDetails.deliverychallan || '';
    });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        this.showFooter = !this.shouldHideFooter(event.url);
      });

    this.showFooter = !this.shouldHideFooter(this.router.url);

    // Session monitoring is now started in loadClientSettings() after settings are loaded
  }

  ngOnDestroy(): void {
    this.clearSessionTimeouts();
    if (this.formDataSubscription) {
      this.formDataSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private clearSessionTimeouts(): void {
    if (this.sessionWarningTimeout) {
      clearTimeout(this.sessionWarningTimeout);
      this.sessionWarningTimeout = null;
    }
    if (this.jwtRefreshTimeout) {
      clearTimeout(this.jwtRefreshTimeout);
      this.jwtRefreshTimeout = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  ngAfterViewInit() {
    if (this.customerNameAutocomplete && this.customerNameAutocomplete.el) {
      const inputElement =
        this.customerNameAutocomplete.el.nativeElement.querySelector('input');
      if (inputElement) {
        // inputElement.addEventListener('input', (event: any) => {
        //   this.onCustomerNameModelChange(event.target.value);
        // });

        inputElement.addEventListener('blur', () => {
          this.onCustomerNameInputBlur();
        });

        inputElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (
              event.key === 'Tab' &&
              this.customerNameAutocomplete.overlayVisible
            ) {
              event.preventDefault();
              event.stopPropagation();
              this.customerNameAutocomplete.hide();

              const nextElement =
                inputElement.parentElement?.parentElement?.nextElementSibling?.querySelector(
                  'input',
                );
              if (nextElement) {
                setTimeout(() => (nextElement as HTMLElement).focus(), 0);
              }
            }
          },
          true,
        ); // Use capture phase to intercept before PrimeNG
      }
    }

    if (this.mobileNumberAutocomplete && this.mobileNumberAutocomplete.el) {
      const inputElement =
        this.mobileNumberAutocomplete.el.nativeElement.querySelector('input');
      if (inputElement) {
        inputElement.setAttribute('maxlength', '10');
        inputElement.setAttribute('inputmode', 'numeric');
        inputElement.setAttribute('pattern', '[0-9]*');

        inputElement.addEventListener('keypress', (event: KeyboardEvent) => {
          const charCode = event.which ? event.which : event.keyCode;
          const isMainKeyboardNumber = charCode >= 48 && charCode <= 57;
          const isNumpadNumber = charCode >= 96 && charCode <= 105;
          const isSpecialKey = [8, 9, 13, 46, 37, 38, 39, 40].includes(
            charCode,
          );

          if (!isMainKeyboardNumber && !isNumpadNumber && !isSpecialKey) {
            event.preventDefault();
          }
        });

        inputElement.addEventListener('paste', (event: ClipboardEvent) => {
          event.preventDefault();
          const pastedText = event.clipboardData?.getData('text') || '';
          const numericValue = pastedText
            .replace(/[^0-9]/g, '')
            .substring(0, 10);

          const target = event.target as HTMLInputElement;
          if (target) {
            target.value = numericValue;
            this.mobileNumber = numericValue;
            this.updateService();
          }
        });

        inputElement.addEventListener('input', (event: any) => {
          const numericValue = event.target.value.replace(/[^0-9]/g, '');
          if (event.target.value !== numericValue) {
            event.target.value = numericValue;
            this.mobileNumber = numericValue;
            this.updateService();
          }
        });

        // Prevent Tab from auto-selecting first item
        inputElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (
              event.key === 'Tab' &&
              this.mobileNumberAutocomplete.overlayVisible
            ) {
              event.preventDefault();
              event.stopPropagation();
              this.mobileNumberAutocomplete.hide();

              // Manually move focus to next field
              const nextElement =
                inputElement.parentElement?.parentElement?.nextElementSibling?.querySelector(
                  'input',
                );
              if (nextElement) {
                setTimeout(() => (nextElement as HTMLElement).focus(), 0);
              }
            }
          },
          true,
        ); // Use capture phase to intercept before PrimeNG
      }
    }
  }

  private shouldHideFooter(url: string): boolean {
    const isDashboard =
      url === '/' ||
      url === '/dashboard' ||
      url.startsWith('/?') ||
      url.startsWith('/dashboard?');

    const shouldHide =
      url.includes('/cash-management') ||
      url.includes('/cash-denomination') ||
      url.includes('/petty-cash') ||
      url.includes('/pdf-template-tester');

    return !isDashboard || shouldHide;
  }

  emitEvent(eventId: string): void {
    this.highlightFooterBox(eventId);

    // Handle logout directly in layout component (works from any route)
    if (eventId === 'F10') {
      this.performLogout();
      return;
    }

    // Handle navigation for F11 and F12
    if (eventId === 'F11') {
      this.router.navigate(['/cash-management']);
      return;
    }

    if (eventId === 'F12') {
      if (!this.isDenominationEnabled) return;
      this.router.navigate(['/cash-denomination']);
      return;
    }

    this.eventService.emitCustomEvent(eventId);
  }

  performLogout(): void {
    const storedStatus = localStorage.getItem('punchStatus');

    if (storedStatus) {
      try {
        this.punchStatus = JSON.parse(storedStatus) as PunchStatus;
        this.handleLogoutWithPunchStatus();
        return;
      } catch {}
    }

    this.cashDenominationService.getPunchStatus().subscribe({
      next: (response) => {
        this.punchStatus = response.data;
        localStorage.setItem('punchStatus', JSON.stringify(this.punchStatus));
        this.handleLogoutWithPunchStatus();
      },
      error: () => {
        this.executeLogout();
      },
    });
  }

  private handleLogoutWithPunchStatus(): void {
    // No Closing Cash prompt where denomination is turned off (feature-flags.ts)
    if (!this.isCounterSessionEnabled || !this.isDenominationEnabled) {
      this.executeLogout();
      return;
    }

    if (!this.punchStatus || this.punchStatus.hasCashOutPunched) {
      this.executeLogout();
      return;
    }

    const isAfterShiftEnd = this.cashDenominationService.isAfterShiftEnd(
      this.punchStatus.shiftEndTime,
    );
    this.canSkipCashOut = !isAfterShiftEnd;
    this.pendingLogoutAction = 'normal';
    this.showCashOutDialog = true;
  }

  private executeLogout(): void {
    this.userName = '';
    this.clearSessionTimeouts();

    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/dashboard']).then(() => {
          window.location.reload();
        });
      },
      error: () => {
        this.router.navigate(['/dashboard']).then(() => {
          window.location.reload();
        });
      },
    });
  }

  onCashOutComplete(event: {
    action: 'saved' | 'skipped';
    tab: string;
    total: number;
  }): void {
    this.showCashOutDialog = false;

    if (event.action === 'saved') {
      if (this.punchStatus) {
        this.punchStatus.hasCashOutPunched = true;
        localStorage.setItem('punchStatus', JSON.stringify(this.punchStatus));
      }
    }

    if (this.pendingLogoutAction === 'normal') {
      this.executeLogout();
    } else if (this.pendingLogoutAction === 'force') {
      this.executeForceLogout();
    }

    this.pendingLogoutAction = null;
  }

  onDateChange(event: any): void {
    this.currentDate = event;
    this.updateService();
  }

  onDateInput(event: any): void {
    const inputValue = event.target.value;

    if (!inputValue || inputValue.trim() === '') {
      this.currentDate = new Date();
      this.updateService();
    }
  }

  onDateBlur(): void {
    if (
      !this.currentDate ||
      !(this.currentDate instanceof Date) ||
      isNaN(this.currentDate.getTime())
    ) {
      this.currentDate = new Date();
      this.updateService();
      return;
    }

    const currentDateOnly = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth(),
      this.currentDate.getDate(),
    );
    const maxDateOnly = new Date(
      this.maxDate.getFullYear(),
      this.maxDate.getMonth(),
      this.maxDate.getDate(),
    );

    if (currentDateOnly > maxDateOnly) {
      this.currentDate = new Date();
      this.updateService();
    }
  }

  shouldShowDatePicker(): boolean {
    return (
      this.clientSettingsService.isDateEditModeEnabled() &&
      !this.isQuotationMode
    );
  }

  onStoreCodeChange(event: any): void {
    this.storeCode = event.target.value;
    this.updateService();
  }

  onCustomerNameChange(event: any): void {
    // Handle both regular input (event.target.value) and autocomplete (direct value or object)
    if (event && event.target) {
      this.customerName = event.target.value;
    } else if (typeof event === 'string') {
      this.customerName = event;
    } else if (event && event.customer_name) {
      // If it's a customer object, extract the name
      this.customerName = event.customer_name;
    }
    this.updateService();
  }

  onSalesRepChange(event: any): void {
    this.salesRep = event.target.value;
    this.updateService();
  }

  // Load pricing groups from API
  async loadPricingGroups(): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    try {
      this.pricingGroups =
        (await this.offerGroupService.getPricingGroups().toPromise()) || [];
      console.log('Layout: Loaded pricing groups:', this.pricingGroups);
    } catch (error) {
      console.error('Failed to load pricing groups in layout:', error);
    }
  }

  // Offer Group dropdown selection
  onOfferGroupSelected(): void {
    console.log('Layout: Pricing group selected:', this.selectedOfferGroupId);
    this.offerGroupService.setSelectedGroup(this.selectedOfferGroupId);
  }

  // Load customers for autocomplete
  async loadCustomers(): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    try {
      this.customers =
        (await this.offerGroupService.getCustomers().toPromise()) || [];
      console.log('Layout: Loaded customers:', this.customers.length);
    } catch (error) {
      console.error('Error loading customers in layout:', error);
      this.customers = [];
    }
  }

  allowAlphaNumeric(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;

    // Allow space (32)
    const isSpace = charCode === 32;

    // Allow numeric (0-9)
    const isNumeric = charCode >= 48 && charCode <= 57;

    // Allow uppercase alphabetic (A-Z)
    const isUppercase = charCode >= 65 && charCode <= 90;

    // Allow lowercase alphabetic (a-z)
    const isLowercase = charCode >= 97 && charCode <= 122;

    if (!isSpace && !isNumeric && !isUppercase && !isLowercase) {
      event.preventDefault();
    }
  }

  // Handle input changes (just update the model, no API calls)
  onMobileNumberInput(event: any): void {
    this.mobileNumber = event.target.value;
    this.updateService();
  }

  // Filter customers for autocomplete
  async filterCustomers(event: any) {
    // Ensure customers are loaded before filtering
    if (this.customers.length === 0) {
      console.log('Customers not loaded yet, loading now...');
      await this.loadCustomers();
    }

    const query = (event.query || '').toLowerCase();
    console.log('Header filterCustomers called with query:', query);
    console.log('Total customers:', this.customers.length);

    if (!query) {
      // Show all customers if no query
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(
        (customer) =>
          customer.customer_name?.toLowerCase().includes(query) ||
          customer.mobile_no?.includes(query),
      );
    }

    console.log('Filtered customers:', this.filteredCustomers.length);
  }

  // Handle customer selection from autocomplete
  onCustomerSelectFromAutocomplete(event: any) {
    const customer = event.value || event;
    if (
      customer &&
      typeof customer === 'object' &&
      'customer_name' in customer
    ) {
      this.customerName = customer.customer_name || '';
      this.mobileNumber = customer.mobile_no || '';
      this.currentCustomer = customer;
      this.customerSearchValue =
        this.customerName && this.mobileNumber
          ? `${this.customerName} - ${this.mobileNumber}`
          : this.customerName || this.mobileNumber;
      this.updateService();

      // Load payment due list
      if (customer.id) {
        this.loadPaymentDueList(customer.id);
      }
    }
  }

  async filterCustomersByMobile(event: any) {
    if (this.customers.length === 0) {
      await this.loadCustomers();
    }

    const query = (event.query || '').toLowerCase();

    if (!query) {
      this.filteredCustomersForMobile = this.customers;
    } else {
      this.filteredCustomersForMobile = this.customers.filter(
        (customer) =>
          customer.mobile_no.includes(query) ||
          customer.customer_name.toLowerCase().includes(query),
      );
    }
  }

  // Handle mobile number selection from autocomplete
  onMobileSelectFromAutocomplete(event: any) {
    const customer = event.value || event;
    if (customer && typeof customer === 'object' && 'mobile_no' in customer) {
      this.mobileNumber = customer.mobile_no || '';
      this.customerName = customer.customer_name || '';
      this.currentCustomer = customer;
      this.updateService();

      // Load payment due list
      if (customer.id) {
        this.loadPaymentDueList(customer.id);
      }
    }
  }

  // Handle customer name model change (when user types)
  private customerNameValidationTimer: any;

  onCustomerNameModelChange(value: any) {
    if (typeof value === 'object' && value !== null) {
      if ('customer_name' in value) {
        this.customerName = value.customer_name || '';
        this.mobileNumber = value.mobile_no || '';
        this.currentCustomer = value;
        this.updateService();

        if (value.id) {
          this.loadPaymentDueList(value.id);
        }
      }
      return;
    }

    const rawStr = typeof value === 'string' ? value : '';
    const strVal = rawStr.trim();

    if (!strVal) {
      this.customerName = '';
      this.mobileNumber = '';
      this.currentCustomer = null;
      this.updateService();
      return;
    }

    // Check if input is digits only AND up to 10 digits (standard mobile number)
    const isMobileNumberInput = /^\d{1,10}$/.test(strVal);

    if (isMobileNumberInput) {
      this.customerName = '';
      this.mobileNumber = strVal;
      this.currentCustomer = null;
      const matchingCustomer = this.customers.find(
        (c) =>
          c.mobile_no === strVal ||
          (c.customer_name &&
            c.customer_name.toLowerCase() === strVal.toLowerCase()),
      );

      if (matchingCustomer) {
        this.customerName = matchingCustomer.customer_name || '';
        this.mobileNumber = matchingCustomer.mobile_no || '';
        this.currentCustomer = matchingCustomer;
        if (matchingCustomer.id) {
          this.loadPaymentDueList(matchingCustomer.id);
        }
      } else {
        // Up to 10 digit number and no customer found -> store in mobileNumber
        this.mobileNumber = strVal;
        this.customerName = '';
        this.currentCustomer = null;
      }
      this.updateService();
      return;
    }

    // Otherwise (contains alphabets OR > 10 digits) -> treat as customerName
    const matchingCustomer = this.customers.find(
      (c) =>
        c.customer_name &&
        c.customer_name.toLowerCase() === strVal.toLowerCase(),
      // ||  c.mobile_no === strVal,
    );

    if (matchingCustomer) {
      this.customerName = matchingCustomer.customer_name || '';
      this.mobileNumber = matchingCustomer.mobile_no || '';
      this.currentCustomer = matchingCustomer;
      if (matchingCustomer.id) {
        this.loadPaymentDueList(matchingCustomer.id);
      }
    } else {
      // Store in customerName field (text or > 10 digits)
      this.customerName = strVal;
      this.mobileNumber = '';
      this.currentCustomer = null;
    }
    this.updateService();

    if (this.customerNameValidationTimer) {
      clearTimeout(this.customerNameValidationTimer);
    }

    this.customerNameValidationTimer = setTimeout(() => {
      this.validateCustomerName();
    }, 500);
  }

  private validateCustomerName() {
    const inputVal = (this.customerName || this.mobileNumber || '').trim();
    if (!inputVal) {
      this.mobileNumber = '';
      this.customerName = '';
      this.currentCustomer = null;
      this.updateService();
      return;
    }

    const isMobileNumberInput = /^\d{1,10}$/.test(inputVal);

    if (isMobileNumberInput) {
      const matchingCustomer = this.customers.find(
        (c) =>
          c.mobile_no === inputVal ||
          (c.customer_name &&
            c.customer_name.toLowerCase() === inputVal.toLowerCase()),
      );

      if (matchingCustomer) {
        this.customerName = matchingCustomer.customer_name || '';
        this.mobileNumber = matchingCustomer.mobile_no || '';
        this.currentCustomer = matchingCustomer;
      } else {
        this.mobileNumber = inputVal;
        this.customerName = '';
        this.currentCustomer = null;
      }
    } else {
      const matchingCustomer = this.customers.find(
        (c) =>
          (c.customer_name &&
            c.customer_name.toLowerCase() === inputVal.toLowerCase()) ||
          c.mobile_no === inputVal,
      );

      if (matchingCustomer) {
        this.customerName = matchingCustomer.customer_name || '';
        this.mobileNumber = matchingCustomer.mobile_no || '';
        this.currentCustomer = matchingCustomer;
      } else {
        this.customerName = inputVal;
        this.mobileNumber = '';
        this.currentCustomer = null;
      }
    }
    this.updateService();
  }

  onCustomerNameInputBlur() {
    if (this.customerNameValidationTimer) {
      clearTimeout(this.customerNameValidationTimer);
    }
    this.validateCustomerName();
  }

  // Prevent Tab from selecting first item in autocomplete
  // Customer names are NOT unique - allow duplicate names unless user explicitly selects from dropdown
  onCustomerNameTabKey(event: any) {
    if (
      this.customerNameAutocomplete &&
      this.customerNameAutocomplete.overlayVisible
    ) {
      this.customerNameAutocomplete.hide();
      event.preventDefault();
      event.stopPropagation();

      const inputElement = event.target as HTMLElement;
      const nextElement =
        inputElement.parentElement?.parentElement?.nextElementSibling?.querySelector(
          'input',
        );
      if (nextElement) {
        setTimeout(() => (nextElement as HTMLElement).focus(), 0);
      }
    }
  }

  private mobileNumberValidationTimer: any;

  onMobileNumberModelChange(value: any) {
    if (typeof value === 'object' && value !== null) {
      if ('mobile_no' in value) {
        this.mobileNumber = value.mobile_no || '';
        this.updateService();
      }
      return;
    }

    this.updateService();

    if (this.mobileNumberValidationTimer) {
      clearTimeout(this.mobileNumberValidationTimer);
    }

    this.mobileNumberValidationTimer = setTimeout(() => {
      if (this.mobileNumber && this.mobileNumber.length === 10) {
        this.lookupCustomerByMobile();
      }
    }, 500);
  }

  // Prevent Tab from selecting first item in autocomplete
  onMobileNumberTabKey(event: any) {
    if (
      this.mobileNumberAutocomplete &&
      this.mobileNumberAutocomplete.overlayVisible
    ) {
      this.mobileNumberAutocomplete.hide();
      event.preventDefault();
      event.stopPropagation();

      const inputElement = event.target as HTMLElement;
      const nextElement =
        inputElement.parentElement?.parentElement?.nextElementSibling?.querySelector(
          'input',
        );
      if (nextElement) {
        setTimeout(() => (nextElement as HTMLElement).focus(), 0);
      }
    }
  }

  // Handle focus out (blur) - trigger customer lookup
  onMobileNumberChange(event: any): void {
    this.mobileNumber = event.target.value;
    this.updateService();

    if (
      this.isOfferGroupMode &&
      this.mobileNumber &&
      this.mobileNumber.length === 10
    ) {
      this.updateService();
    }

    if (this.mobileNumber && this.mobileNumber.length === 10) {
      this.lookupCustomerByMobile();
    }
  }

  // Lookup customer by mobile number
  lookupCustomerByMobile(): void {
    this.productService.getAllCustomers().subscribe({
      next: (response) => {
        if (
          (response.status === 201 || response.status === '201') &&
          response.data
        ) {
          const customer = response.data.find(
            (c: Customer) => c.mobile_no === this.mobileNumber,
          );
          if (customer) {
            this.currentCustomer = customer;
            if (!this.customerName || this.customerName?.trim() === '') {
              this.customerName = customer.customer_name;
              this.updateService();
            }
            // Load payment due list
            this.loadPaymentDueList(customer.id);
          } else {
            this.currentCustomer = null;
            this.creditOutstanding = '0.00';
          }
        }
      },
      error: (error) => {
        console.error('Error looking up customer:', error);
        this.currentCustomer = null;
        this.creditOutstanding = '0.00';
      },
    });
  }

  // Load payment due list for customer
  loadPaymentDueList(customerId: number): void {
    this.productService.getPaymentDueList(customerId).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          const totalDue = response.data.reduce(
            (sum, item) => sum + item.balanceAmountDue,
            0,
          );
          this.creditOutstanding = totalDue.toFixed(2);
        } else {
          this.creditOutstanding = '0.00';
        }
      },
      error: (error) => {
        console.error('Error loading payment due list:', error);
        this.creditOutstanding = '0.00';
      },
    });

    this.loadCustomerLoyaltyPoints(customerId);
  }

  loadCustomerLoyaltyPoints(customerId: number): void {
    // Check loyalty feature is enabled
    if (!this.clientSettingsService.isLoyaltyPointsEnabled()) {
      this.resetLoyaltyData();
      return;
    }

    this.productService.getCustomerLoyaltyPoints(customerId).subscribe({
      next: (response) => {
        if (response.status === 201 && response.data) {
          this.customerLoyaltyData = response.data;
          this.loyaltyPoints = response.data.rewardpoints || 0;

          const redeemAmount =
            response.data.loyality_membership?.redeem_amount || 0;
          this.loyaltyAmount = this.loyaltyPoints * redeemAmount;
          this.isLoyaltyEnabled = true;
        } else {
          this.resetLoyaltyData();
        }
      },
      error: (error) => {
        console.error('Error loading customer loyalty points:', error);
        this.resetLoyaltyData();
      },
    });
  }

  resetLoyaltyData(): void {
    this.customerLoyaltyData = null;
    this.loyaltyPoints = 0;
    this.loyaltyAmount = 0;
    this.isLoyaltyEnabled = false;
  }

  hasLoyaltyPoints(): boolean {
    return !!(
      this.isLoyaltyEnabled &&
      this.customerLoyaltyData &&
      this.loyaltyPoints > 0 &&
      this.loyaltyAmount > 0
    );
  }

  isLoyaltyExpired(): boolean {
    if (!this.customerLoyaltyData?.loyality_membership?.expiry_date) {
      return false; // No expiry date means never expires
    }

    const expiryDate = new Date(
      this.customerLoyaltyData.loyality_membership.expiry_date,
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);

    return expiryDate < today;
  }

  getLoyaltyExpiryDays(): number {
    if (!this.customerLoyaltyData?.loyality_membership?.expiry_date) {
      return -1; // No expiry
    }

    const expiryDate = new Date(
      this.customerLoyaltyData.loyality_membership.expiry_date,
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);

    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  getLoyaltyExpiryText(): string {
    if (!this.customerLoyaltyData?.loyality_membership?.expiry_date) {
      return 'No Expiry';
    }

    const daysRemaining = this.getLoyaltyExpiryDays();

    if (daysRemaining < 0) {
      return 'Expired';
    } else if (daysRemaining === 0) {
      return 'Expires Today';
    } else if (daysRemaining === 1) {
      return 'Expires Tomorrow';
    } else if (daysRemaining <= 7) {
      return `Expires in ${daysRemaining} days`;
    } else if (daysRemaining <= 30) {
      return `Expires in ${daysRemaining} days`;
    } else {
      const expiryDate = new Date(
        this.customerLoyaltyData.loyality_membership.expiry_date,
      );
      return `Expires on ${expiryDate.toLocaleDateString('en-GB')}`;
    }
  }

  getLoyaltyExpiryClass(): string {
    const daysRemaining = this.getLoyaltyExpiryDays();

    if (daysRemaining < 0) {
      return 'text-danger'; // Expired
    } else if (daysRemaining <= 7) {
      return 'text-danger'; // Expiring soon
    } else if (daysRemaining <= 30) {
      return 'text-warning'; // Expiring within a month
    } else {
      return 'text-success'; // Valid
    }
  }

  hasOutstandingCredit(): boolean {
    return !!(
      this.currentCustomer &&
      this.creditOutstanding &&
      parseFloat(this.creditOutstanding) > 0
    );
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  highlightFooterBox(eventId: string): void {
    this.removeHighlight();
    const footerBoxIndex = parseInt(eventId.substring(1), 10);
    const footerBox = this.el.nativeElement.querySelector(
      `.footer-box:nth-child(${footerBoxIndex})`,
    );
    if (footerBox && !footerBox.classList.contains('disabled')) {
      // Only highlight if not disabled
      this.selectedFooterBox = new ElementRef(footerBox);
      this.renderer.addClass(this.selectedFooterBox.nativeElement, 'selected');

      // Auto-remove highlight after 1 second for F11 and F12
      if (eventId === 'F11' || eventId === 'F12') {
        setTimeout(() => {
          this.removeHighlight();
        }, 1000);
      }
    }
  }

  removeHighlight(): void {
    if (this.selectedFooterBox) {
      this.renderer.removeClass(
        this.selectedFooterBox.nativeElement,
        'selected',
      );
      this.selectedFooterBox = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.selectedFooterBox &&
      !this.selectedFooterBox.nativeElement.contains(event.target)
    ) {
      this.removeHighlight();
    }
    if (this.showUserMenu) {
      this.showUserMenu = false;
    }
  }

  private updateService(): void {
    this.formDataService.updateFormData({
      currentDate: this.currentDate,
      empCode: this.empCode,
      storeCode: this.storeCode,
      mobileNumber: this.mobileNumber,
      customerName: this.customerName,
      salesRep: this.salesRep,
      customerId: this.currentCustomer?.id,
    });
  }

  /**
   * Explicitly clears a PrimeNG p-autoComplete widget's native input element.
   * PrimeNG caches its display value internally and does not flush the DOM input
   * when [(ngModel)] is set programmatically (e.g. after payment clears customer info).
   * Without this, the header continues to show the previous customer name/number.
   */
  private clearAutoCompleteInput(autocomplete: any): void {
    if (!autocomplete) return;
    // Clear via PrimeNG's writeValue API (updates internal state + input)
    if (typeof autocomplete.writeValue === 'function') {
      autocomplete.writeValue('');
    }
    // Also clear the native input element directly as a safety net
    const inputEl = autocomplete?.el?.nativeElement?.querySelector('input');
    if (inputEl) {
      inputEl.value = '';
    }
  }

  logout() {
    this.eventService.emitCustomEvent('logout');
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;

    if (this.showUserMenu) {
      setTimeout(() => {
        const menuElement = document.querySelector(
          '.user-profile-menu',
        ) as HTMLElement;
        const profileElement = document.querySelector(
          '.user-profile',
        ) as HTMLElement;

        if (menuElement && profileElement) {
          const profileRect = profileElement.getBoundingClientRect();
          const menuHeight = menuElement.offsetHeight;
          const windowHeight = window.innerHeight;

          const spaceBelow = windowHeight - profileRect.bottom;

          // Right-align the menu with the avatar pill
          menuElement.style.right = `${window.innerWidth - profileRect.right}px`;

          if (spaceBelow < menuHeight + 20) {
            menuElement.style.bottom = `${windowHeight - profileRect.top + 8}px`;
            menuElement.style.top = 'auto';
          } else {
            menuElement.style.top = `${profileRect.bottom + 8}px`;
            menuElement.style.bottom = 'auto';
          }

          menuElement.style.right = `${window.innerWidth - profileRect.right}px`;
        }
      }, 0);
    }
  }

  handleLogout(): void {
    this.showUserMenu = false;
    this.emitEvent('F10');
  }

  /** Shown for every vertical: "Add another account" lives in this list, so hiding it until a second account exists left non-mobile terminals unable to add one */
  get showSwitchCompany(): boolean {
    return true;
  }

  toggleAccountList(event: Event): void {
    event.stopPropagation();
    this.posAccounts = this.accountRegistry.list();
    this.showAccountList = !this.showAccountList;
  }

  switchCompany(account: PosAccountOption, event: Event): void {
    event.stopPropagation();
    if (account.active || !this.confirmLeaveBill()) return;
    this.showUserMenu = false;
    this.accountRegistry.switchTo(account.id);
  }

  addCompanyAccount(event: Event): void {
    event.stopPropagation();
    if (!this.confirmLeaveBill()) return;
    this.showUserMenu = false;
    this.accountRegistry.beginAddAccount();
  }

  /** The bill on screen is not saved anywhere; switching company would drop it */
  private confirmLeaveBill(): boolean {
    const items = this.accountRegistry.pendingItems();
    return items === 0 || window.confirm(
      `The current bill has ${items} item(s). Switching company clears it; hold the bill first if you need it. Continue?`,
    );
  }

  loadClientSettings(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.clientSettingsService.loadClientSettings().subscribe((settings) => {
      this.clientSettingsService.permissions$.subscribe((permissions) => {
        this.modePermissions = permissions;
      });
      this.isBarcodeEnabled = this.clientSettingsService.isBarcodeEnabled();
      this.isMobileMandatory =
        this.clientSettingsService.isCustomerMobileMandatory();
      this.isShippingDetailsEnabled =
        this.clientSettingsService.isShippingDetailsEnabled();
      this.isCounterSessionEnabled =
        this.clientSettingsService.isCounterSessionEnabled();

      if (!this.clientSettingsService.isDateEditModeEnabled()) {
        this.currentDate = new Date();
        this.updateService();
      }

      // Start session monitoring AFTER client settings are loaded
      // This ensures isCounterSessionEnabled is set before checking - PLEASE DO NOT CHANGE THE ORDER OF THIS LINE
      this.startSessionMonitoring();
    });
  }

  // Customer form dialog methods
  hasCustomerInfo(): boolean {
    return !!(
      this.customerName &&
      this.customerName?.trim() !== '' &&
      this.mobileNumber &&
      this.mobileNumber?.trim() !== ''
    );
  }

  openCustomerDialog(): void {
    this.customerDialogMode = this.hasCustomerInfo() ? 'edit' : 'add';
    this.showCustomerDialog = true;
  }

  handleCustomerSave(customerData: any): void {
    if (!this.currentCustomer) {
      this.currentCustomer = {} as Customer;
    }

    this.currentCustomer.id = customerData.id;
    this.currentCustomer.customer_name =
      customerData.customer_name || 'Walk in Customer';
    this.currentCustomer.mobile_no = customerData.mobile_no;
    this.currentCustomer.sellingpricegroup_id =
      customerData.sellingpricegroup_id;
    this.currentCustomer.pan_no = customerData.pan_no;
    this.currentCustomer.gst_no = customerData.gst_no;
    this.currentCustomer.contact_person = customerData.contact_person;
    this.currentCustomer.address_line1 = customerData.address;
    this.currentCustomer.address_line2 = customerData.address2;
    this.currentCustomer.address_line3 = customerData.address3;
    this.currentCustomer.city = customerData.city;
    this.currentCustomer.state = customerData.state;
    this.currentCustomer.country = customerData.country;
    this.currentCustomer.zip_code = customerData.zip_code;
    this.currentCustomer.fax_no = customerData.fax_no;
    this.currentCustomer.email = customerData.email;

    this.customerName = customerData.customer_name;
    this.mobileNumber = customerData.mobile_no;

    // Update customerSearchValue so autocomplete [(ngModel)] displays the customer
    this.customerSearchValue =
      this.customerName && this.mobileNumber
        ? `${this.customerName} - ${this.mobileNumber}`
        : this.customerName || this.mobileNumber;

    // Flush PrimeNG autocomplete display value
    if (this.customerNameAutocomplete) {
      if (typeof this.customerNameAutocomplete.writeValue === 'function') {
        this.customerNameAutocomplete.writeValue(this.customerSearchValue);
      }
    }

    // Immediately add or update customer in local customers list for autocomplete suggestions
    if (this.customers) {
      const existingIndex = this.customers.findIndex(
        (c) => c.id === customerData.id,
      );
      if (existingIndex > -1) {
        this.customers[existingIndex] = {
          ...this.customers[existingIndex],
          ...this.currentCustomer,
        };
      } else {
        this.customers.unshift({ ...this.currentCustomer });
      }
    }

    this.updateService();

    if (customerData.id) {
      this.loadPaymentDueList(customerData.id);
    }

    if (customerData.sellingpricegroup_id) {
      this.selectedOfferGroupId = customerData.sellingpricegroup_id;
      this.offerGroupService.setSelectedGroup(
        customerData.sellingpricegroup_id,
      );
      this.offerGroupService.setOfferGroupMode(true);
    }

    this.loadCustomers();

    this.showCustomerDialog = false;
  }

  handleCustomerCancel(): void {
    this.showCustomerDialog = false;
  }

  getProcessButtonText(): string {
    if (this.isExchangeOrder) {
      return 'Process Exchange';
    } else if (this.isQuotationMode) {
      return 'Process Quotation';
    } else {
      return 'Process Order';
    }
  }

  // Shipping Details dialog methods
  openShippingDialog(): void {
    // if (!this.isShippingDetailsEnabled) {
    //   this.showShippingDialog = false;
    //   return;
    // }
    this.resetShippingPincodeValidation();
    this.showShippingDialog = true;
  }

  /** Keeps only digits, at most 6, and flags when something else was typed or pasted */
  onShippingPincodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
    // Dropping a 7th digit is not an error; dropping a letter or symbol is
    this.shippingPincodeCharsBlocked = /\D/.test(input.value);
    if (cleaned !== input.value) {
      input.value = cleaned;
    }
    this.shippingPincode = cleaned;
  }

  onShippingPincodeBlur(): void {
    this.shippingPincodeTouched = true;
    this.shippingPincodeCharsBlocked = false; // the field only holds digits by now
  }

  get shippingPincodeError(): string {
    if (this.shippingPincodeCharsBlocked) {
      return 'Only digits (0-9) are allowed in the pincode.';
    }
    const pin = this.shippingPincode || '';
    if (pin && !/^[0-9]{6}$/.test(pin) && (this.shippingPincodeTouched || this.shippingSaveAttempted)) {
      return 'Pincode must be exactly 6 digits.';
    }
    return '';
  }

  private resetShippingPincodeValidation(): void {
    this.shippingPincodeCharsBlocked = false;
    this.shippingPincodeTouched = false;
    this.shippingSaveAttempted = false;
  }

  saveShippingDetails(): void {
    this.shippingSaveAttempted = true;
    if (this.shippingPincode && !/^[0-9]{6}$/.test(this.shippingPincode)) {
      return; // keep the dialog open; the inline pincode message explains why
    }

    // Combine shipping address fields with semicolon separator
    const addressParts = [
      this.shippingAddress1,
      this.shippingAddress2,
      this.shippingCity,
      this.shippingState,
      this.shippingPincode,
    ].filter((part) => part && part.trim() !== '');

    const shippingAddress = addressParts.join(';');

    // Update form data service with shipping details
    this.formDataService.updateShippingDetails({
      shippingaddress: shippingAddress,
      lrno: this.transportLrNo,
      vehicleno: this.transportVehicleNo,
      transportername: this.transporterName,
      deliverychallan: this.transportDeliveryChallan,
    });

    this.showShippingDialog = false;
  }

  cancelShippingDialog(): void {
    this.resetShippingPincodeValidation();
    this.showShippingDialog = false;
  }

  resetShippingForm(): void {
    this.shippingAddress1 = '';
    this.shippingAddress2 = '';
    this.shippingCity = '';
    this.shippingState = '';
    this.shippingPincode = '';
    this.resetShippingPincodeValidation();
    this.transportLrNo = '';
    this.transportVehicleNo = '';
    this.transporterName = '';
    this.transportDeliveryChallan = '';
    this.formDataService.resetShippingDetails();
  }

  private startSessionMonitoring(): void {
    // Only enable session monitoring if client setting is enabled
    if (!this.isCounterSessionEnabled) {
      console.log(
        '[SESSION] Counter session monitoring disabled via client settings',
      );
      return;
    }

    const sessionStatus = this.authService.getSessionStatus();

    if (!sessionStatus.sessionEndTime) {
      console.log('[SESSION] No session data available for monitoring');
      return;
    }

    if (!sessionStatus.isWithinSession) {
      this.showSessionWarning = true;
      this.sessionWarningType = 'outside';
      this.sessionWarningMessage = 'You are logged in outside your shift hours';
      return;
    }

    const sessionDuration = this.authService.getSessionDurationMinutes();
    const minutesRemaining = sessionStatus.minutesRemaining;

    if (sessionDuration <= 10) {
      this.showTimeoutWarning(minutesRemaining);
      this.performTokenRefreshWithRetry();
      this.startCountdownInterval();
      return;
    }

    if (minutesRemaining <= 10) {
      this.showTimeoutWarning(minutesRemaining);
      this.performTokenRefreshWithRetry();
      this.startCountdownInterval();
      return;
    }

    const msUntilWarning = (minutesRemaining - 10) * 60 * 1000;
    console.log(
      `Setting session warning timeout for ${minutesRemaining - 10} minutes from now`,
    );

    this.sessionWarningTimeout = setTimeout(() => {
      const currentStatus = this.authService.getSessionStatus();
      this.showTimeoutWarning(currentStatus.minutesRemaining);
      this.performTokenRefreshWithRetry();
      this.startCountdownInterval();
    }, msUntilWarning);

    if (sessionStatus.jwtExpiresBeforeSession) {
      const msUntilJwtRefresh = Math.max(
        0,
        (sessionStatus.jwtMinutesRemaining - 5) * 60 * 1000,
      );
      console.log(
        `Setting JWT refresh timeout for ${sessionStatus.jwtMinutesRemaining - 5} minutes from now`,
      );

      this.jwtRefreshTimeout = setTimeout(() => {
        this.performSilentTokenRefresh();
      }, msUntilJwtRefresh);
    }
  }

  private showTimeoutWarning(minutesRemaining: number): void {
    this.showSessionWarning = true;
    this.sessionWarningType = 'timeout';
    this.sessionWarningMessage = `Your shift session ends in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}`;
  }

  private startCountdownInterval(): void {
    this.countdownInterval = setInterval(() => {
      const sessionStatus = this.authService.getSessionStatus();

      if (sessionStatus.minutesRemaining <= 0) {
        this.showSessionWarning = true;
        this.sessionWarningType = 'critical';
        this.sessionWarningMessage =
          'Your shift session has ended. Please save your work.';
        this.stopCountdownInterval();
      } else {
        this.sessionWarningMessage = `Your shift session ends in ${sessionStatus.minutesRemaining} minute${sessionStatus.minutesRemaining !== 1 ? 's' : ''}`;
      }
    }, 60000);
  }

  private stopCountdownInterval(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private performSilentTokenRefresh(): void {
    this.authService.refreshToken().subscribe({
      next: (response) => {
        if (!response.error) {
          console.log('Silent token refresh successful');
        }
      },
      error: (err) => {
        console.error('Silent token refresh failed:', err);
      },
    });
  }

  private performTokenRefreshWithRetry(): void {
    this.authService.refreshToken().subscribe({
      next: (response) => {
        if (response.error) {
          this.handleRefreshTokenFailure();
        } else {
          console.log('Token refresh successful on session warning');
          this.refreshTokenRetryCount = 0;
        }
      },
      error: () => {
        this.handleRefreshTokenFailure();
      },
    });
  }

  private handleRefreshTokenFailure(): void {
    this.refreshTokenRetryCount++;

    if (this.refreshTokenRetryCount === 1) {
      console.log('Token refresh failed, retrying...');
      setTimeout(() => {
        this.performTokenRefreshWithRetry();
      }, 2000);
    } else {
      this.showSessionWarning = true;
      this.sessionWarningType = 'error';
      this.sessionWarningMessage =
        'Session refresh failed. Please save your receipt (F4) to avoid data loss.';
      this.stopCountdownInterval();
    }
  }

  dismissSessionWarning(): void {
    if (
      this.sessionWarningType !== 'critical' &&
      this.sessionWarningType !== 'error'
    ) {
      this.showSessionWarning = false;
    }
  }

  forceLogout(): void {
    const storedStatus = localStorage.getItem('punchStatus');

    if (storedStatus) {
      try {
        this.punchStatus = JSON.parse(storedStatus) as PunchStatus;
        this.handleForceLogoutWithPunchStatus();
        return;
      } catch {}
    }

    this.cashDenominationService.getPunchStatus().subscribe({
      next: (response) => {
        this.punchStatus = response.data;
        localStorage.setItem('punchStatus', JSON.stringify(this.punchStatus));
        this.handleForceLogoutWithPunchStatus();
      },
      error: () => {
        this.executeForceLogout();
      },
    });
  }

  private handleForceLogoutWithPunchStatus(): void {
    if (!this.isCounterSessionEnabled || !this.isDenominationEnabled) {
      this.executeForceLogout();
      return;
    }
    if (!this.punchStatus || this.punchStatus.hasCashOutPunched) {
      this.executeForceLogout();
      return;
    }

    const isAfterShiftEnd = this.cashDenominationService.isAfterShiftEnd(
      this.punchStatus.shiftEndTime,
    );
    this.canSkipCashOut = !isAfterShiftEnd;
    this.pendingLogoutAction = 'force';
    this.showCashOutDialog = true;
  }

  /**
   * Execute the actual force logout after Cash Out (if required)
   */
  private executeForceLogout(): void {
    this.clearSessionTimeouts();
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/login']);
        window.location.reload();
      },
      error: () => {
        this.authService.logoutSync();
        this.router.navigate(['/login']);
        window.location.reload();
      },
    });
  }
}
